import { Wrap, Box, VisuallyHidden } from '@chakra-ui/react'
import type { UploadResult, UppyEventMap, UppyOptions } from '@uppy/core'
import type Uppy from '@uppy/core'
import DropTarget from '@uppy/drop-target'
import FileInput from '@uppy/file-input'
import StatusBar from '@uppy/status-bar'
import sortBy from 'lodash/sortBy'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@app/components/ui/button'
import { toaster } from '@app/components/ui/toaster'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import FilesSectionContent from '@app/pages/maps/components/filesSectionContent'
import useOpenAiUppyInstance from '@app/pages/strategies/components/aiChat/useOpenAiUppyInstance'
import Can from '@app/shared/authorization/can'
import type { FileTicketMeta, RemoteFile } from '@app/shared/uploader/types'
import withSuspenseWrapper from '@app/shared/withSuspenseWrapper'
import type { DomainBusinessContext } from '@app/types'
import ICON_MAP from '@app/utils/iconMap'
import { useAiAssistantFileCacheFilesAddMutation } from '@graphql/queries'
interface Props {
  businessContext?: DomainBusinessContext | null
  loading?: boolean
}

const extraUppyOptions: Partial<UppyOptions<FileTicketMeta, RemoteFile>> = {
  // Uppy restricts uploads of the same file multiple times, however. We want to show the progress bars, so we can't just
  // clear them. Because the "files" in the FileSectionContent are different from the uppy files, we can't just remove them
  // by id too because they are different objects. This method just says allow the file to be added again.
  // https://uppy.io/docs/uppy/#how-do-i-allow-duplicate-files
  onBeforeFileAdded: () => true
}

const FilesSection: FC<Props> = ({ businessContext = null, loading = false }) => {
  const [, addFiles] = useAiAssistantFileCacheFilesAddMutation()
  const files = useGetObjectsByProperties('aiAssistantFile', {
    cacheableId: businessContext?.id,
    cacheableType: 'BusinessContext'
  })
  const sorted = useMemo(() => sortBy(files, (f) => f.name.toLowerCase()), [files])
  const [uppyInstance, setUppyInstance] = useState<Uppy<FileTicketMeta, RemoteFile>>(null)

  const dragDropRef = useRef(null)
  const statusBarRef = useRef(null)
  const fileInputRef = useRef(null)

  const onComplete: UppyEventMap<FileTicketMeta, RemoteFile>['complete'] = useCallback(
    async (result: UploadResult<FileTicketMeta, RemoteFile>) => {
      if (!businessContext?.id) {
        return
      }

      try {
        addFiles({
          input: {
            cacheableId: businessContext?.id,
            cacheableType: 'BusinessContext',
            fileIds: result.successful.map((file) => file.response.body.id)
          }
        })
      } catch (e) {
        toaster.create({
          type: 'error',
          title: 'Failed to upload files',
          description: e.message
        })
      }
    },
    [addFiles, businessContext?.id]
  )

  const uppy = useOpenAiUppyInstance({ onComplete, extraUppyOptions })

  if (!uppyInstance) {
    setUppyInstance(uppy)
  }

  useEffect(() => {
    if (!uppyInstance) {
      return
    }

    if (dragDropRef?.current) {
      uppyInstance.use(DropTarget, {
        target: dragDropRef.current
      })
    }

    if (statusBarRef?.current) {
      uppyInstance.use(StatusBar, {
        target: statusBarRef.current
      })
    }

    if (fileInputRef?.current) {
      uppyInstance.use(FileInput, {
        target: fileInputRef.current,
        pretty: false
      })
    }
  }, [uppyInstance])

  const handlePickFileClick = () => {
    fileInputRef?.current?.querySelector('input[type="file"]').click()
  }

  return (
    <Box ref={dragDropRef} w="full" position="relative">
      <Box>
        <Wrap>
          {sorted?.length > 0 && <FilesSectionContent loading={loading} files={sorted} />}

          <Can I="create" a="businessContext">
            <Box my={1}>
              <Button aria-label="Upload files" type="button" size="xs" variant="surface" onClick={handlePickFileClick}>
                <ICON_MAP.Upload /> Upload files
              </Button>
            </Box>
          </Can>
        </Wrap>
        <Box w="full" ref={statusBarRef} />
        <VisuallyHidden>
          <Box ref={fileInputRef} />
        </VisuallyHidden>
      </Box>
    </Box>
  )
}
export default withSuspenseWrapper(FilesSection)
